import React from "react";

import styled from "@emotion/styled";
import { colors } from "@zapalacorp/tokens";
import { AiOutlineCloseCircle } from "react-icons/ai";

export type BannerType = "neutral" | "info" | "warning" | "negative" | "positive";

interface BannerProps {
  type?: BannerType;
  prefix?: React.ReactNode;
  label: string;
  onClose?: () => void;
}

const getBannerColors = (type: BannerType) => {
  switch (type) {
    case "neutral":
      return {
        color: colors.gray700,
        borderColor: colors.gray200,
        background: "transparent",
      };
    case "info":
      return {
        color: colors.brandBlue700,
        borderColor: colors.brandBlue200,
        background: colors.brandBlue50,
      };
    case "warning":
      return {
        color: colors.warning700,
        borderColor: colors.warning200,
        background: colors.warning50,
      };
    case "negative":
      return {
        color: colors.error700,
        borderColor: colors.error200,
        background: colors.error50,
      };
    case "positive":
      return {
        color: colors.success700,
        borderColor: colors.success200,
        background: colors.success50,
      };
    default:
      return {
        color: colors.gray700,
        borderColor: colors.gray700,
        background: "transparent",
      };
  }
};

interface StyledProps {
  type: BannerType;
}

const BannerWrapper = styled("div")<StyledProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ type }) => getBannerColors(type).borderColor};
  background: ${({ type }) => getBannerColors(type).background};
`;

const BannerContent = styled("div")<StyledProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  color: ${({ type }) => getBannerColors(type).color};
  font-family: Manrope, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400; /* Consider 500 for more emphasis if needed */
  line-height: 21px; /* Adjusted for improved readability */
  letter-spacing: 0.25px;
`;

const CloseButton = styled("div")<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ type }) => getBannerColors(type).color};
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

export const Banner: React.FC<BannerProps> = ({ type = "neutral", prefix, label, onClose }) => {
  return (
    <BannerWrapper type={type}>
      <BannerContent type={type}>
        {prefix && <>{prefix}</>}
        <span>{label}</span>
      </BannerContent>
      {onClose && (
        <CloseButton type={type} onClick={onClose}>
          <AiOutlineCloseCircle size={16} />
        </CloseButton>
      )}
    </BannerWrapper>
  );
};
